import { useEffect } from 'react';

// Code-review note: the useEffect below deliberately omits `jsonLd` from its
// dep array and uses `jsonLdKey` as its stable identity. See
// /app/REVIEW_WAIVERS.md §1 & §2 before flagging exhaustive-deps here.

/**
 * Dynamically manages per-page SEO tags: title, meta description, canonical,
 * Open Graph tags, and JSON-LD structured data. Cleans up on unmount.
 *
 * @param {Object} config
 * @param {string} config.title - Page title
 * @param {string} config.description - Meta description
 * @param {string} config.canonical - Full canonical URL
 * @param {string} [config.ogImage]
 * @param {Array} [config.jsonLd] - Array of structured-data objects to inject
 * @param {string} [config.keywords]
 */
export function useSEO({ title, description, canonical, ogImage, jsonLd = [], keywords, geo }) {
  const jsonLdKey = JSON.stringify(jsonLd);
  const geoKey = geo ? `${geo.placename || ''}|${geo.position || ''}|${geo.region || ''}` : '';
  useEffect(() => {
    const originalTitle = document.title;
    if (title) document.title = title;

    const dynamicEls = [];
    const setMeta = (attrName, attrValue, content) => {
      let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
      const isNew = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        el.setAttribute('data-dynamic-seo', 'true');
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      if (isNew) dynamicEls.push(el);
      return el;
    };

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (keywords) {
      setMeta('name', 'keywords', keywords);
    }
    if (ogImage) {
      setMeta('property', 'og:image', ogImage);
      setMeta('name', 'twitter:image', ogImage);
    }
    // Geo meta tags — helps map/local searches ("lawn mowing near Alfredton").
    // Always clear any existing geo tags first so navigating from Alfredton to
    // (say) the home page doesn't leak stale geo data.
    const GEO_TAG_NAMES = ['geo.placename', 'geo.region', 'geo.position', 'ICBM'];
    GEO_TAG_NAMES.forEach((n) => {
      const stale = document.head.querySelector(`meta[name="${n}"]`);
      if (stale) stale.remove();
    });
    if (geo) {
      if (geo.placename) setMeta('name', 'geo.placename', geo.placename);
      if (geo.region) setMeta('name', 'geo.region', geo.region);
      if (geo.position) {
        setMeta('name', 'geo.position', geo.position);
        setMeta('name', 'ICBM', geo.position.replace(';', ', '));
      }
    }

    // Canonical link
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute('href', canonical);
      setMeta('property', 'og:url', canonical);
    }

    // JSON-LD injections
    const scripts = jsonLd.map((data, idx) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-dynamic-seo', String(idx));
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = originalTitle;
      scripts.forEach((s) => s.remove());
      // Remove newly-created dynamic meta tags so route changes don't
      // leak per-page tags (e.g. geo.*) into the next page's <head>.
      dynamicEls.forEach((el) => el.remove());
    };
    // `jsonLd` intentionally omitted: `jsonLdKey` is the stable identity for the
    // (possibly-new-reference-each-render) `jsonLd` object. Including it would
    // re-run the effect every render and thrash the <head>.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, keywords, jsonLdKey, geoKey]);
}
